import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import Header from "components/common/Header/Header";
import PaginationComponent from "components/common/Pagination/index";
import Search from "components/common/Search/Search";
import SelectOption from "components/common/Select/Select";
import SideBarNew from "components/common/SideBar/SideBarNew";
import {
  REPORTING_BREADCRUMB,
  REPORTING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import { emptyPaginationObj } from "Constants/constant";
import { LoginContext } from "helpers/LoginContext";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  HiOutlineDocumentDownload,
  HiOutlineRefresh,
} from "react-icons/hi";
import { FiDelete } from "react-icons/fi";
import { toast } from "react-toastify";
import { changeDateToYearMonthDay, classNames } from "Utils/utils";
import { fetchReportExports } from "store/services/reportExport.services";
import {
  downloadReportExportFile,
  formatRemainingTime,
  getExportStatusColor,
  getExportStatusLabel,
  REPORT_TYPE_LABELS,
} from "../reportExportUtils";

const POLL_INTERVAL_MS = 4000;

const PER_PAGE_VALUES = [
  { label: "10", value: 10 },
  { label: "15", value: 15 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
];

const STATUS_OPTIONS = [
  { label: "All statuses", value: "all" },
  { label: "Queued", value: "queued" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
];

const FORMAT_OPTIONS = [
  { label: "All formats", value: "all" },
  { label: "PDF", value: "pdf" },
  { label: "Excel", value: "xlsx" },
];

const REPORT_TYPE_OPTIONS = [
  { label: "All reports", value: "all" },
  ...Object.entries(REPORT_TYPE_LABELS).map(([value, label]) => ({
    label,
    value,
  })),
];

const defaultFilters = () => ({
  search: "",
  status: STATUS_OPTIONS[0],
  format: FORMAT_OPTIONS[0],
  reportType: REPORT_TYPE_OPTIONS[0],
  fromDate: null,
  toDate: null,
});

const ExportHistory = () => {
  const { sideBarPadding } = useContext(LoginContext);
  const [exports, setExports] = useState([]);
  const [loader, setLoader] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_VALUES[1]);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [downloadingUuid, setDownloadingUuid] = useState(null);
  const [filters, setFilters] = useState(() => defaultFilters());
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const pollRef = useRef(null);

  const hasActiveExports = exports.some((item) =>
    ["queued", "processing"].includes(item.status)
  );

  const hasActiveFilterValues = (filterValues = appliedFilters) =>
    Boolean(
      filterValues.search?.trim() ||
        filterValues.status?.value !== "all" ||
        filterValues.format?.value !== "all" ||
        filterValues.reportType?.value !== "all" ||
        filterValues.fromDate ||
        filterValues.toDate
    );

  const loadExports = useCallback(
    async (silent = false) => {
      if (!silent) setLoader(true);
      try {
        const res = await fetchReportExports({
          page,
          perPage: perPage?.value ?? 15,
          search: appliedFilters.search,
          status: appliedFilters.status?.value,
          format: appliedFilters.format?.value,
          report_type: appliedFilters.reportType?.value,
          date_from: appliedFilters.fromDate
            ? changeDateToYearMonthDay(appliedFilters.fromDate)
            : undefined,
          date_to: appliedFilters.toDate
            ? changeDateToYearMonthDay(appliedFilters.toDate)
            : undefined,
        });
        const data = res?.data?.data;
        setExports(data?.exports || []);
        setPaginationObj({
          page: data?.pagination?.current_page ?? 1,
          total: data?.pagination?.total ?? 0,
          totalPages: data?.pagination?.last_page ?? 1,
          per_page: data?.pagination?.per_page ?? perPage?.value ?? 15,
        });
      } catch (error) {
        if (!silent) {
          toast.error("Could not load export history.");
        }
      } finally {
        if (!silent) setLoader(false);
      }
    },
    [page, perPage, appliedFilters]
  );

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  useEffect(() => {
    if (hasActiveExports) {
      pollRef.current = setInterval(() => loadExports(true), POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActiveExports, loadExports]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const handleClearFilters = () => {
    const cleared = defaultFilters();
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const handleDownload = async (item) => {
    if (!item.download_url || downloadingUuid) return;

    setDownloadingUuid(item.uuid);
    try {
      await downloadReportExportFile(item.download_url, {
        format: item.format,
        report_label: item.report_label,
      });
    } catch {
      // Toast feedback is handled inside downloadReportExportFile.
    } finally {
      setDownloadingUuid(null);
    }
  };

  const renderProgress = (item) => {
    if (!["queued", "processing"].includes(item.status)) {
      return null;
    }

    const progress = item.progress ?? 0;

    return (
      <div className="mt-2">
        <div className="mb-1 flex justify-between text-xs text-gray-500">
          <span>{progress}% complete</span>
          <span>{formatRemainingTime(item.remaining_seconds)}</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-blue-500 transition-all duration-500"
            style={{ width: `${Math.max(progress, 5)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-full">
      <SideBarNew />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col`}
      >
        <main className="flex-1 min-h-screen bg-gray-50">
          <Header
            title="Export History"
            breadcrumb={
              <Breadcrumb
                pages={REPORTING_BREADCRUMB(
                  REPORTING_BREADCRUMB_PAGES.EXPORT_HISTORY
                )}
              />
            }
          />
          <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  Export History
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  Large reports are generated in the background. Track progress
                  here and download when ready. Files are kept for 72 hours.
                </p>
              </div>
              <CustomButton
                label="Refresh"
                variant="secondary"
                Icon={<HiOutlineRefresh className="h-4 w-4" />}
                clickHandler={() => loadExports()}
              />
            </div>

            <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                <div className="xl:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Search
                  </label>
                  <Search
                    search={filters.search}
                    setSearch={(value) =>
                      setFilters((prev) => ({ ...prev, search: value }))
                    }
                    placeholder="Search by report name"
                    width="w-full"
                    customClassSearch="w-full"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Requested date
                  </label>
                  <DateRangePickerNew
                    fromDate={filters.fromDate}
                    toDate={filters.toDate}
                    handleChange={(dates) => {
                      const [start, end] = dates;
                      setFilters((prev) => ({
                        ...prev,
                        fromDate: start,
                        toDate: end,
                      }));
                    }}
                    placeholder="Select date range"
                    cssClass="w-full"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Status
                  </label>
                  <SelectOption
                    name="status"
                    data={STATUS_OPTIONS}
                    selectedOption={filters.status}
                    handleChange={(option) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: option || STATUS_OPTIONS[0],
                      }))
                    }
                    isSearch={false}
                    isClearable={false}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Format
                  </label>
                  <SelectOption
                    name="format"
                    data={FORMAT_OPTIONS}
                    selectedOption={filters.format}
                    handleChange={(option) =>
                      setFilters((prev) => ({
                        ...prev,
                        format: option || FORMAT_OPTIONS[0],
                      }))
                    }
                    isSearch={false}
                    isClearable={false}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Report type
                  </label>
                  <SelectOption
                    name="report_type"
                    data={REPORT_TYPE_OPTIONS}
                    selectedOption={filters.reportType}
                    handleChange={(option) =>
                      setFilters((prev) => ({
                        ...prev,
                        reportType: option || REPORT_TYPE_OPTIONS[0],
                      }))
                    }
                    isSearch={true}
                    isClearable={false}
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <CustomButton
                  label="Apply filters"
                  variant="primary"
                  clickHandler={handleApplyFilters}
                />
                <CustomButton
                  label="Clear"
                  variant="secondary"
                  Icon={<FiDelete className="h-4 w-4" />}
                  clickHandler={handleClearFilters}
                  isDisabled={!hasActiveFilterValues(filters)}
                />
              </div>
            </div>

            {hasActiveExports && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                One or more reports are still processing. This page refreshes
                automatically every few seconds.
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              {loader ? (
                <div className="flex justify-center py-16">
                  <CircularLoader />
                </div>
              ) : exports.length === 0 ? (
                <div className="px-6 py-16 text-center">
                  <HiOutlineDocumentDownload className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <p className="text-gray-600">
                    {hasActiveFilterValues()
                      ? "No exports match your filters."
                      : "No exports yet."}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {hasActiveFilterValues()
                      ? "Try adjusting or clearing your filters."
                      : "When you export a large report, it will appear here with status and download link."}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {exports.map((item) => (
                    <div
                      key={item.uuid}
                      className="px-4 py-4 sm:px-6 sm:py-5"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-medium text-gray-900">
                              {item.report_label}
                            </h3>
                            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase text-gray-600">
                              {item.format}
                            </span>
                            <span
                              className={classNames(
                                "rounded-full px-2.5 py-0.5 text-xs font-medium",
                                getExportStatusColor(item.status)
                              )}
                            >
                              {getExportStatusLabel(item.status)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Requested{" "}
                            {item.created_at
                              ? moment(item.created_at).format(
                                  "DD MMM YYYY, h:mm A"
                                )
                              : "—"}
                            {item.row_count
                              ? ` · ${item.row_count.toLocaleString()} rows`
                              : ""}
                          </p>
                          {item.status === "failed" && item.error_message && (
                            <p className="mt-2 text-sm text-red-600">
                              {item.error_message}
                            </p>
                          )}
                          {renderProgress(item)}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {item.status === "completed" && item.download_url && (
                            <>
                              <CustomButton
                                label={
                                  downloadingUuid === item.uuid
                                    ? "Downloading…"
                                    : "Download"
                                }
                                variant="primary"
                                showLoading={downloadingUuid === item.uuid}
                                isDisabled={!!downloadingUuid}
                                Icon={
                                  downloadingUuid !== item.uuid ? (
                                    <HiOutlineDocumentDownload className="h-4 w-4" />
                                  ) : null
                                }
                                clickHandler={() => handleDownload(item)}
                              />
                              {downloadingUuid === item.uuid && (
                                <span className="text-xs text-gray-500">
                                  Preparing your file…
                                </span>
                              )}
                            </>
                          )}
                          {["queued", "processing"].includes(item.status) && (
                            <span className="text-sm text-gray-500">
                              {formatRemainingTime(item.remaining_seconds)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!loader && paginationObj.total > 0 && (
              <div className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <PaginationComponent
                  newDesign={true}
                  showPerPageDropdown={true}
                  isClearable={false}
                  perPageSelectCssClass="w-20 min-w-[5rem] max-w-[5rem]"
                  perPageMenuMinWidth="90px"
                  page={page}
                  activeListLength={exports}
                  limit={perPage?.value ?? 15}
                  totalList={paginationObj.total}
                  totalPages={paginationObj.totalPages}
                  perPageData={PER_PAGE_VALUES}
                  perPageValue={perPage}
                  handleLimitChange={(selectedPerPage) => {
                    setPerPage(selectedPerPage || PER_PAGE_VALUES[1]);
                    setPage(1);
                  }}
                  handlePagination={(nextPage) => {
                    setPage(nextPage);
                  }}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ExportHistory;
